import { type ReactElement, type ReactNode, useState } from 'react';
import { Text, View } from 'react-native';

import { UnseenCue } from '../index';

import type { useTheme } from './theme';
import type { makeStyles } from './loggerStyles';
import type { LoggerMode } from './loggerMode';
import { LoggerHeaderActions } from './LoggerHeaderActions';
import { TitleButton } from './TitleButton';
import { WorkoutNotesPanel } from './WorkoutNotesPanel';
import { RoundTypeBadge } from '../primitives/RoundTypeBadge';
import { InlineStatusHint } from '../primitives/InlineStatusHint';
import { Button } from '../primitives/Button';
import { GONE_HERE_KEY } from './useSaveWorkout';
import { saveErrorText } from './saveWriter';
import type { LoggerDraft } from './state/loggerDraftTypes';
import { hashNotesContent, useNotesRead } from './useNotesRead';

// Logger header: title-left + right icon cluster (LoggerHeaderActions.tsx).
// #318: «Info» on the left opens the coach notes. Extracted verbatim
// out of LoggerScreen (Track R).
// Presentational: every action routes back through the passed handlers.
// postSaveSummary mode reuses the cluster minus save, with the close icon
// acting as Done.
type LoggerHeaderProps = {
  postSaveSummary: { sessionId: string; onDone: () => void } | null;
  /** The mode contract (PE-22 slice A). Replaces two identical
   *  re-derivations of `editing.mode !== 'repeat'` that used to live in
   *  this file under two different names. */
  mode: LoggerMode;
  /** WHICH prescription surface this is — the mode alone cannot say, and the
   *  athlete needs the word (PE-22 K2, Antenore 2026-08-26).
   *
   *  `'prescribe'` covers two different jobs: planning a single session (a
   *  calendar sits above the card) and planning a multi-week program (the
   *  weeks × days matrix sits above it). The MOUNT knows which — the core
   *  does not, and should not: the same seam as `onPrescribeEffort`.
   *
   *  ⛔ Read only when `mode === 'prescribe'`; absent everywhere else. */
  prescribing?: 'plan' | 'program';
  submitting: boolean;
  entriesLength: number;
  onMinimize?: () => void;
  onSave: () => void;
  onCancel: () => void;
  /** #45: the save's failure, shown here beside Save. `error` is a
   *  `mobile.logger.*` key or a text; `serverError` is the server's verdict. */
  error?: string | null;
  serverError?: string | null;
  /** Saves the open draft as a new workout; offered with the «session
   *  deleted elsewhere» error when the host has no queue to recover from. */
  onSaveAsNew?: () => void;
  /** Arm a rest countdown on demand (header clock, live logging only). */
  onStartRest?: () => void;
  /** The active exercise is part of a superset. The clock lights up then
   *  (MX-5 D9): inside a group the automatic rest is the 4s implement change,
   *  so "start a real rest" is the only moment that affordance answers a
   *  question the athlete is actually asking. */
  inSuperset?: boolean;
  /** Read-only coach context (program description + the day's session
   *  note), opened from the program line. */
  coachNotes?: { programDescription?: string; sessionNote?: string };
  /** AP-9b: an unstarted «today» draft: its status line. */
  todayDraft?: boolean;
  /** The plan's or program's name: the notes panel's title. */
  templateName?: string;
  /** #336 S2: the program round's type (LoggerDraft.roundType). Only a
   *  deload round shows, as its badge beside «Info»; display only. */
  roundType?: LoggerDraft['roundType'];
  /** #386: the «Date & duration» card, first in the Info panel. Absent
   *  while the card has nothing to show (an unstarted today draft). */
  dateEditor?: ReactNode;
  styles: ReturnType<typeof makeStyles>;
  theme: ReturnType<typeof useTheme>;
  t: (k: string, opts?: Record<string, unknown>) => string;
};

export function LoggerHeader({
  postSaveSummary,
  mode,
  prescribing,
  submitting,
  entriesLength,
  onMinimize,
  onSave,
  onCancel,
  error = null,
  serverError = null,
  onSaveAsNew,
  onStartRest,
  inSuperset = false,
  coachNotes,
  todayDraft = false,
  templateName,
  roundType,
  dateEditor,
  styles,
  theme,
  t,
}: LoggerHeaderProps): ReactElement {
  const hasCoachNotes = Boolean(
    coachNotes &&
      (coachNotes.programDescription || coachNotes.sessionNote),
  );
  const [notesOpen, setNotesOpen] = useState(false);
  // Per-device read flag: pulse the Info chevron until the athlete
  // opens it once. Content-hashed so an edited coach note re-pulses.
  const contentKey = hasCoachNotes
    ? hashNotesContent([
        coachNotes?.programDescription,
        coachNotes?.sessionNote,
      ])
    : null;
  const { read, markRead } = useNotesRead(contentKey);
  // R7/R8 — the announcement is <UnseenCue>, shared with the ✕ that declares
  // gear missing: it breathes when motion is allowed and shows a static mark
  // when it is not. What stayed here is only WHEN the notes count as seen.
  const cueSeen = read || !hasCoachNotes;
  const onToggleNotes = () => {
    setNotesOpen((v) => !v);
    if (!read) markRead();
  };
  // Header = STATUS, not a title (Antenore 2026-06-06): the workout name is
  // edited in the form just below, so repeating it here is noise. Show an
  // "Edit" status only when editing a past session; a live new/repeat log
  // shows no status.
  // PE-22 K0 — `isTrueEdit` (`mode !== 'log'`) used to answer all four
  // questions below at once, and 'prescribe' splits them: it is NOT a past
  // session (so no "Edit" status) and it is NOT live (so no
  // rest clock). One boolean cannot say both, which is the same collapse
  // loggerMode.ts was written to undo. So the two questions are asked
  // separately, each by its exact literal. For 'log'/'log-past' the pair is
  // bit-for-bit what `isTrueEdit`/`!isTrueEdit` produced.
  const isPastEdit = mode === 'log-past';
  const isLive = mode === 'log';
  // PE-22 K2 — the third and fourth statuses. The header NAMES THE THING you
  // are writing (a Plan, a Program), which is what keeps it a status and not a
  // title: same rule that lets "Edit workout" sit here. It used to say nothing
  // at all in prescription, and the emptiness was the only cue that you were
  // not logging — negative space, which reduced vision does not read.
  // ⛔ No new row: this is the slot "Edit workout" already occupies.
  const prescribeStatus =
    mode === 'prescribe' && prescribing !== undefined
      ? prescribing === 'program'
        ? t('mobile.logger.programTitle', { defaultValue: 'Program' })
        : t('mobile.logger.planTitle', { defaultValue: 'Plan' })
      : null;
  const titleStatus = postSaveSummary
    ? t('mobile.postSession.summaryTitle', { defaultValue: 'Workout saved' })
    : isPastEdit
      ? t('mobile.logger.editTitle')
      : isLive && todayDraft
        ? t('mobile.logger.draftToday.status', { defaultValue: 'Draft · not saved' })
        : prescribeStatus;
  const notesA11y = t('mobile.logger.notesToggle', {
    defaultValue: 'Notes for this workout',
  });
  // #318: «Info» is always there while logging or correcting, notes or not;
  // the panel says when there are none. Not after a save, and not while
  // writing a plan or program: its notes are being written, not read.
  const showInfo = !postSaveSummary && mode !== 'prescribe';
  const infoLabel = t('mobile.logger.infoButton', { defaultValue: 'Info' });
  // Not after a save: the summary speaks of the session done, not the plan.
  const deload = !postSaveSummary && roundType === 'deload';
  return (
    <View>
      <View style={styles.header}>
        <View style={styles.titleCol}>
          {titleStatus ? (
            // `accessibilityRole="header"` — the house idiom for a top-bar
            // title (EditorTopBar, SectionHeader, MetricBlock). Without it the
            // status is still read in linear order, but it is not a landmark:
            // navigating BY HEADINGS cannot jump to it, so the one word that
            // says which job you are doing is the one thing you must cross the
            // whole bar to reach. Raised by the a11y audit of this changeset;
            // the gap predates it — «Modifica workout» had it too — and one
            // line covers all four statuses at once.
            <Text style={styles.title} accessibilityRole="header">
              {titleStatus}
            </Text>
          ) : null}
          {showInfo || deload ? (
            <View style={styles.infoRow}>
              {showInfo ? (
                <TitleButton
                  text={infoLabel}
                  accessibilityLabel={`${infoLabel}. ${notesA11y}`}
                  expanded={notesOpen}
                  onPress={onToggleNotes}
                  hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
                  renderChevron={(chevron) => (
                    <UnseenCue theme={theme} seen={cueSeen}>
                      {chevron}
                    </UnseenCue>
                  )}
                  fit
                  style={styles.programLineBtn}
                  theme={theme}
                />
              ) : null}
              {deload ? <RoundTypeBadge theme={theme} type="deload" /> : null}
            </View>
          ) : null}
        </View>
        <LoggerHeaderActions
          postSaveSummary={postSaveSummary}
          isLive={isLive}
          submitting={submitting}
          entriesLength={entriesLength}
          todayDraft={todayDraft}
          inSuperset={inSuperset}
          onMinimize={onMinimize}
          onSave={onSave}
          onCancel={onCancel}
          onStartRest={onStartRest}
          styles={styles}
          theme={theme}
          t={t}
        />
        {error !== null || serverError !== null ? (
          <View style={styles.saveError}>
            {error !== null ? (
              <InlineStatusHint
                theme={theme}
                tone="error"
                text={saveErrorText(error, t)}
              />
            ) : null}
            {error === GONE_HERE_KEY && onSaveAsNew ? (
              <Button
                theme={theme}
                label={t('mobile.recovery.saveAsNew')}
                onPress={onSaveAsNew}
                disabled={submitting}
                loading={submitting}
              />
            ) : null}
            {serverError !== null ? (
              // The server's verdict, one node. One assertive announcement
              // at a time: it waits behind `error`.
              <InlineStatusHint
                theme={theme}
                tone="error"
                title={t('mobile.logger.serverErrorTitle')}
                text={serverError}
                hint={t('mobile.logger.serverErrorHint')}
                live={error !== null ? 'polite' : true}
              />
            ) : null}
          </View>
        ) : null}
      </View>
      {showInfo && notesOpen ? (
        <WorkoutNotesPanel
          coachNotes={coachNotes ?? {}}
          title={templateName}
          dateEditor={dateEditor}
          styles={styles}
          t={t}
        />
      ) : null}
    </View>
  );
}
